import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GlobalLoadingService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingCount = 0;

  constructor() {}

  /**
   * Observable to track global loading state
   */
  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  /**
   * Get current loading state
   */
  get isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Set loading state to true
   * This will disable all buttons across the application
   */
  setLoading(): void {
    this.loadingCount++;
    if (this.loadingCount === 1) {
      this.loadingSubject.next(true);
    }
  }

  /**
   * Set loading state to false
   * This will re-enable all buttons across the application
   */
  clearLoading(): void {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    if (this.loadingCount === 0) {
      this.loadingSubject.next(false);
    }
  }

  /**
   * Reset loading state completely
   * Useful for error scenarios or manual reset
   */
  resetLoading(): void {
    this.loadingCount = 0;
    this.loadingSubject.next(false);
  }

  /**
   * Execute a function with loading state management
   * Automatically sets loading before execution and clears after completion
   */
  async executeWithLoading<T>(fn: () => Promise<T>): Promise<T> {
    this.setLoading();
    try {
      const result = await fn();
      return result;
    } finally {
      this.clearLoading();
    }
  }

  /**
   * Execute a function with loading state management for synchronous operations
   * Automatically sets loading before execution and clears after completion
   */
  executeWithLoadingSync<T>(fn: () => T): T {
    this.setLoading();
    try {
      const result = fn();
      return result;
    } finally {
      this.clearLoading();
    }
  }
}
